// src/hooks/common/useTableActions.js
import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const useTableActions = ({
    basePath,
    deleteSingle,
    deleteAll,
    restoreSingle,
    restoreAll,
    forceDeleteSingle,
    forceDeleteAll,
    confirmSingle,
    closeSingle,
    exportSingle,
    isArchive = false,
    // allow caller to override the default sort column and direction
    defaultSortBy = "id",
    defaultSortDir = "desc",
}) => {
    const navigate = useNavigate();

    // State Management
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [search, setSearch] = useState("");
    // initialize sorting state using provided defaults
    const [sortBy, setSortBy] = useState(defaultSortBy);
    const [sortDir, setSortDir] = useState(defaultSortDir); // direction default may be overridden as well
    const [selectedIds, setSelectedIds] = useState([]);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);

    // Sorting
    const handleRequestSort = useCallback((columnId) => {
        setSortBy((prevSortBy) => {
            if (prevSortBy === columnId) {
                // نفس العمود، نعكس الاتجاه: desc → asc → desc
                setSortDir((prev) => (prev === "desc" ? "asc" : "desc"));
                return columnId;
            } else {
                // ✅ التعديل 2: عمود جديد، نبدأ بـ desc (السهم لفوق)
                setSortDir("desc");
                return columnId;
            }
        });
    }, []);

    // Row Selection
    const handleRowCheckboxClick = useCallback((event, id) => {
        event.stopPropagation();
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    }, []);

    const handleSelectAllClick = useCallback((event, items) => {
        if (event.target.checked) {
            const allIds = items.map((item) => item.id);
            setSelectedIds(allIds);
        } else {
            setSelectedIds([]);
        }
    }, []);

    // ✅ تعريف handleMenuClose أولاً
    const handleMenuClose = useCallback(() => {
        setMenuAnchorEl(null);
        setSelectedItem(null);
    }, []);

    // Menu Actions
    const handleMenuOpen = useCallback((event, item) => {
        setMenuAnchorEl(event.currentTarget);
        setSelectedItem(item);
    }, []);

    // ✨ NEW: View Action
    const handleView = useCallback(() => {
        if (!selectedItem) return;
        navigate(`${basePath}/${selectedItem.id}`);
        handleMenuClose();
    }, [selectedItem, basePath, navigate, handleMenuClose]);

    // Edit Action
    const handleEdit = useCallback(() => {
        if (!selectedItem) return;
        navigate(`${basePath}/${selectedItem.id}/edit`);
        handleMenuClose();
    }, [selectedItem, basePath, navigate, handleMenuClose]);

    // Delete Action
    const handleMenuDelete = useCallback(() => {
        if (!selectedItem) return;

        if (isArchive) {
            forceDeleteSingle?.(selectedItem.id, {
                onSuccess: () => {
                    setSelectedIds([]);
                },
            });
        } else {
            deleteSingle?.(selectedItem.id, {
                onSuccess: () => {
                    setSelectedIds([]);
                },
            });
        }

        handleMenuClose();
    }, [selectedItem, isArchive, forceDeleteSingle, deleteSingle, handleMenuClose]);

    // Restore Action
    const handleMenuRestore = useCallback(() => {
        if (!selectedItem) return;
        restoreSingle?.(selectedItem.id, {
            onSuccess: () => {
                setSelectedIds([]);
            },
        });
        handleMenuClose();
    }, [selectedItem, restoreSingle, handleMenuClose]);

    // Confirm Action
    const handleMenuConfirm = useCallback(() => {
        if (!selectedItem) return;
        confirmSingle?.(selectedItem.id, {
            onSuccess: () => {
                setSelectedIds([]);
            },
        });
        handleMenuClose();
    }, [selectedItem, confirmSingle, handleMenuClose]);

    // Close Action
    const handleMenuCloseAction = useCallback(() => {
        if (!selectedItem) return;
        closeSingle?.(selectedItem.id, {
            onSuccess: () => {
                setSelectedIds([]);
            },
        });
        handleMenuClose();
    }, [selectedItem, closeSingle, handleMenuClose]);

    // Export Action
    const handleMenuExport = useCallback(() => {
        if (!selectedItem) return;
        exportSingle?.(selectedItem.id);
        handleMenuClose();
    }, [selectedItem, exportSingle, handleMenuClose]);

    // Bulk Delete
    const handleBulkDelete = useCallback(() => {
        if (!selectedIds.length) return;

        if (isArchive) {
            forceDeleteAll?.({ items: selectedIds });
        } else {
            deleteAll?.({ items: selectedIds });
        }

        setSelectedIds([]);
    }, [selectedIds, isArchive, forceDeleteAll, deleteAll]);

    // Bulk Restore
    const handleBulkRestore = useCallback(() => {
        if (!selectedIds.length) return;
        restoreAll?.({ items: selectedIds });
        setSelectedIds([]);
    }, [selectedIds, restoreAll]);

    // Pagination
    const handlePageChange = useCallback((value) => {
        setPage(value);
    }, []);

    const handlePerPageChange = useCallback((value) => {
        setPerPage(value);
        setPage(1);
    }, []);

    // Clear Selection
    const clearSelection = useCallback(() => {
        setSelectedIds([]);
    }, []);

    return {
        // State
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        selectedItem,

        // State Setters
        setPage,
        setPerPage,
        setSearch,
        setSortBy,
        setSortDir,
        setSelectedIds,

        // Handlers
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleView,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleMenuConfirm,
        handleMenuCloseAction,
        handleMenuExport,
        handleBulkDelete,
        handleBulkRestore,
        handlePageChange,
        handlePerPageChange,
        clearSelection,
    };
};

export default useTableActions;
